import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { PageWrapper } from '../components/PageWrapper';
import { Textarea } from '../components/ui/textarea';
import {
  getEntry,
  getSettings,
  formatDisplayDate,
  addCommentToEntry,
  updateEntryWithAIResponse,
  isAICompanionEnabled,
} from '../utils/storage';
import { getDailyCompletionMessage } from '../utils/aiCompanion';

const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

export const HistoricalView = () => {
  const navigate = useNavigate();
  const { date } = useParams();
  const settings = getSettings();

  const [entry, setEntry] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const loaded = getEntry(date);
    if (loaded) {
      setEntry(loaded);
    }
    setIsLoaded(true);
  }, [date]);

  if (!isLoaded) {
    return (
      <PageWrapper className="bg-app">
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 size={24} className="animate-spin text-secondary" />
        </div>
      </PageWrapper>
    );
  }

  if (!entry) {
    return (
      <PageWrapper className="bg-app">
        <div className="flex flex-col items-center justify-center min-h-screen px-6">
          <p className="text-secondary font-body text-sm">Entry not found</p>
          <button
            onClick={() => navigate('/history')}
            className="mt-8 underline text-wisteria-600 font-body text-sm"
          >
            Back to History
          </button>
        </div>
      </PageWrapper>
    );
  }

  const goals = entry.goalSnapshots || [];
  const prompts = entry.promptSnapshots || [];
  const comments = entry.comments || [];

  const handleSaveComment = async () => {
    if (!commentText.trim()) return;

    setIsSavingComment(true);
    const fallbackMessage = 'Thanks for adding to your reflection.';
    let responseMessage = fallbackMessage;
    let shouldPersistResponse = false;

    // Add comment to entry
    addCommentToEntry(date, commentText);
    
    // Generate AI response with comment context
    const aiEnabled = isAICompanionEnabled();
    if (aiEnabled) {
      try {
        // Prepare data for AI including the comment
        const formData = {
          goals: entry.goals || {},
          answers: {},
        };
        prompts.forEach(p => {
          formData.answers[p.answerKey] = entry[p.answerKey] || '';
        });

        const aiResult = await getDailyCompletionMessage({
          formData,
          goals,
          prompts,
          fallbackMessage,
        });
        if (aiResult?.message) {
          responseMessage = aiResult.message;
        }
        if (aiResult?.usedAI && aiResult?.message) {
          shouldPersistResponse = true;
        }
      } catch (error) {
        console.error('Error generating AI response for comment:', error);
      }
    }

    // Only persist when a real AI response was generated.
    if (shouldPersistResponse) {
      updateEntryWithAIResponse(date, responseMessage);
    }

    // Navigate to AIResponse screen
    navigate('/ai-response', {
      state: {
        response: responseMessage,
        userName: settings?.name || 'friend',
        returnTo: `/historical-view/${date}`,
      },
    });

    setCommentText('');
    setIsSavingComment(false);
  };

  return (
    <PageWrapper className="bg-app">
      <div className="max-w-md mx-auto px-6 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <button
            onClick={() => navigate('/history')}
            className="p-2 rounded-full hover:bg-wisteria-50 transition-colors"
            data-testid="historical-back-btn"
            aria-label="Go back"
          >
            <ArrowLeft size={24} strokeWidth={1.5} className="text-secondary" />
          </button>
          <h1 className="font-heading text-2xl font-light text-primary">
            {formatDisplayDate(date)}
          </h1>
        </motion.div>

        {/* Goals Section */}
        {goals.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70 mb-4">
              Goals
            </h2>
            <div className="space-y-2">
              {goals.map((goal, index) => {
                const status = entry.goals?.[goal.id];
                const isCompleted = status?.completed || status?.easyActivity;
                const displayText = status?.easyActivity ? goal.easyText || goal.text : goal.text;

                if (!isCompleted) return null;

                return (
                  <motion.div
                    key={goal.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + index * 0.05 }}
                    className="flex items-center gap-3 p-3 rounded-xl bg-wisteria-100 border border-wisteria-200"
                  >
                    <span className="text-wisteria-700 font-medium">✓</span>
                    <span className="font-body text-sm text-primary">{cap(displayText)}</span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Reflections Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8 space-y-6"
        >
          <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70 mb-4">
            Reflections:
          </h2>
          {prompts.map((prompt, index) => {
            const value = entry[prompt.answerKey];
            if (!value || value === '–') return null;

            return (
              <motion.div
                key={prompt.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 + index * 0.05 }}
              >
                <p className="font-body text-sm text-secondary mb-2">{prompt.question}:</p>
                <p className="font-body text-sm text-primary leading-relaxed bg-theme-light rounded-2xl p-4 border border-theme-secondary">
                  {value}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* AI Response Section */}
        {entry.aiResponse && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8 p-4 rounded-2xl bg-wisteria-50 border border-wisteria-200"
          >
            <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70 mb-3">
              AI Reflection
            </h2>
            <p className="font-body text-sm text-primary leading-relaxed">
              {entry.aiResponse}
            </p>
          </motion.div>
        )}

        {/* Comments Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mb-8"
        >
          <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70 mb-4 text-center">
            Comments:
          </h2>

          {comments.length > 0 && (
            <div className="space-y-3 mb-6">
              {comments.map((comment, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.05 }}
                  className="p-4 rounded-2xl bg-theme-light border border-theme-secondary"
                >
                  <p className="font-body text-sm text-primary mb-2">{comment.text}</p>
                  <p className="font-body text-xs text-secondary/60">
                    {formatDisplayDate(comment.date)}
                  </p>
                </motion.div>
              ))}
            </div>
          )}

          {/* Add Comment Input */}
          <div className="space-y-3">
            <Textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a comment..."
              className="bg-theme-light border-theme-secondary rounded-2xl p-4 focus:ring-theme-primary font-body min-h-[100px]"
              data-testid="comment-input"
            />
            <button
              onClick={handleSaveComment}
              disabled={!commentText.trim() || isSavingComment}
              className="w-full bg-wisteria-200 text-wisteria-900 rounded-full px-8 py-3 font-body font-medium transition-all hover:bg-wisteria-300 hover:-translate-y-0.5 shadow-sm disabled:opacity-50"
              data-testid="save-comment-btn"
            >
              {isSavingComment ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </span>
              ) : (
                'Save Comment'
              )}
            </button>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex gap-3 py-8"
        >
          <button
            onClick={() => navigate(`/daily-log?date=${date}&mode=edit`)}
            className="flex-1 bg-theme-light border border-theme-secondary text-primary rounded-full px-6 py-3 font-body font-medium transition-all hover:bg-wisteria-50 hover:-translate-y-0.5"
            data-testid="edit-btn"
          >
            Edit Entry
          </button>
        </motion.div>
      </div>
    </PageWrapper>
  );
};

export default HistoricalView;
